import React, { useEffect, useState } from "react"
import Skeleton from "react-loading-skeleton"
import { useNavigate } from "react-router-dom"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  PieChart,
  Pie,
  ResponsiveContainer,
} from "recharts"
import {
  FiActivity,
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiHelpCircle,
  FiInbox,
  FiLayers,
  FiMessageSquare,
} from "react-icons/fi"
import { getInquiryByClientId, getTicketsByAgent, getTicketCountsByAgentId } from "../../../services/AgentServices"
import { FaCheckCircle, FaInbox, FaSpinner } from "react-icons/fa"

const COLORS = ["#2563eb", "#60a5fa", "#0f766e", "#94a3b8"]

const normalizeStatus = (status) => String(status || "").toUpperCase().trim()

const normalizeStatusCode = (status) => {
  const value = normalizeStatus(status)
  if (["OPEN", "OPN"].includes(value)) return "OPN"
  if (["IN-PROGRESS", "INPROGRESS", "IN_PROGRESS", "INP"].includes(value)) return "INP"
  if (["RESOLVED", "CLOSED", "CRS", "DONE"].includes(value)) return "CRS"
  if (["NO-RESPONSE", "NO RESPONSE", "NORESPONSE", "CNR"].includes(value)) return "CNR"
  return value
}

const isAssignedInquiryForUser = (inquiry, userId) => {
  const normalizedUserId = Number.parseInt(userId)
  const assignedId = Number.parseInt(inquiry?.assigned_to ?? inquiry?.assignedTo, 10)
  return assignedId === normalizedUserId
}

const summaryItems = [
  { key: "Open", label: "Open", icon: <FaInbox /> },
  { key: "In-Progress", label: "In Progress", icon: <FaSpinner /> },
  { key: "Resolved", label: "Resolved", icon: <FaCheckCircle /> },
  { key: "No-Response", label: "No Response", icon: <FaCheckCircle /> },
]

const Dashboard = () => {
  const [ticketData, setTicketData] = useState({
    total_count: 0,
    Open: 0,
    Inprogress: 0,
    Resolved: 0,
    Noresponse: 0,
  })
  const [inquiryData, setInquiryData] = useState({
    total_count: 0,
    Open: 0,
    Inprogress: 0,
    Resolved: 0,
    Noresponse: 0,
  })
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent <= 0) return null
    const radius = innerRadius + (outerRadius - innerRadius) / 2
    const radian = Math.PI / 180
    const x = cx + radius * Math.cos(-midAngle * radian)
    const y = cy + radius * Math.sin(-midAngle * radian)

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={700}
      >
        {(percent * 100).toFixed(0)}%
      </text>
    )
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const userId = sessionStorage.getItem("userId")
        const clientId = sessionStorage.getItem("clientId")

        const [ticketCountRes, ticketRes, inquiryRes] = await Promise.all([
          getTicketCountsByAgentId(userId),
          getTicketsByAgent(clientId, userId),
          clientId ? getInquiryByClientId(clientId, 1, 1000) : Promise.resolve(null),
        ])

        const tickets = Array.isArray(ticketRes?.tickets) ? ticketRes.tickets : []
        const ticketCountData = ticketCountRes?.data || {}
        const ticketCounts = {
          totalCount: ticketCountData.totalCount ?? tickets.length,
          openCount:
            ticketCountData.openCount ??
            tickets.filter((t) => normalizeStatusCode(t.status) === "OPN").length,
          inProgressCount:
            ticketCountData.inProgressCount ??
            tickets.filter((t) => normalizeStatusCode(t.status) === "INP").length,
          closedCount:
            ticketCountData.closedCount ??
            tickets.filter((t) => normalizeStatusCode(t.status) === "CRS").length,
          noResponseCount:
            ticketCountData.noResponseCount ??
            tickets.filter((t) => normalizeStatusCode(t.status) === "CNR").length,
        }

        setTicketData({
          total_count: ticketCounts.totalCount || 0,
          Open: ticketCounts.openCount || 0,
          Inprogress: ticketCounts.inProgressCount || 0,
          Resolved: ticketCounts.closedCount || 0,
          Noresponse: ticketCounts.noResponseCount || 0,
        })

        if (inquiryRes && (inquiryRes.status || inquiryRes.success)) {
          const allInquiries = Array.isArray(inquiryRes.data) ? inquiryRes.data : []
          const assigned = allInquiries.filter((inq) => isAssignedInquiryForUser(inq, userId))
          const open = assigned.filter((i) => normalizeStatusCode(i.status || i.status_code || "OPN") === "OPN").length
          const inProgress = assigned.filter((i) => normalizeStatusCode(i.status || i.status_code) === "INP").length
          const resolved = assigned.filter((i) => normalizeStatusCode(i.status || i.status_code) === "CRS").length
          const noResponse = assigned.filter((i) => normalizeStatusCode(i.status || i.status_code) === "CNR").length

          setInquiryData({
            total_count: open + inProgress + resolved + noResponse,
            Open: open,
            Inprogress: inProgress,
            Resolved: resolved,
            Noresponse: noResponse,
          })
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const ticketSummary = [
    { name: "Open", value: ticketData.Open },
    { name: "In-Progress", value: ticketData.Inprogress },
    { name: "Resolved", value: ticketData.Resolved },
    { name: "No-Response", value: ticketData.Noresponse },
  ]

  const inquirySummary = [
    { name: "Open", value: inquiryData.Open },
    { name: "In-Progress", value: inquiryData.Inprogress },
    { name: "Resolved", value: inquiryData.Resolved },
    { name: "No-Response", value: inquiryData.Noresponse },
  ]

  const ticketBarData = ticketSummary.map((item, index) => ({
    name: item.name,
    count: item.value,
    fill: COLORS[index % COLORS.length],
  }))

  const hasAssignedTickets = ticketData.total_count > 0
  const hasAssignedInquiries = inquiryData.total_count > 0
  const totalOpen = ticketData.Open + inquiryData.Open
  const totalResolved = ticketData.Resolved + inquiryData.Resolved
  const totalInProgress = ticketData.Inprogress + inquiryData.Inprogress
  const totalWork = ticketData.total_count + inquiryData.total_count

  const overviewStats = [
    {
      key: "tickets",
      label: "Tickets",
      value: ticketData.total_count,
      sublabel: "Assigned",
      icon: <FiLayers />,
      accent: "primary",
      show: ticketData.total_count > 0,
    },
    {
      key: "inquiries",
      label: "Inquiries",
      value: inquiryData.total_count,
      sublabel: "Assigned",
      icon: <FiMessageSquare />,
      accent: "success",
      show: inquiryData.total_count > 0,
    },
    {
      key: "open",
      label: "Open Items",
      value: totalOpen,
      sublabel: "Needs action",
      icon: <FiClock />,
      accent: "warning",
      show: totalOpen > 0,
    },
    {
      key: "resolved",
      label: "Resolved",
      value: totalResolved,
      sublabel: "Completed",
      icon: <FiCheckCircle />,
      accent: "teal",
      show: totalResolved > 0,
    },
  ].filter((item) => item.show)

  const handleBarClick = (statusName) => {
    navigate("/agent/components/Tickets", {
      state: { filterFromDashboard: statusName },
    })
  }

  const handleTicketPieClick = (_, index) => {
    const statusMap = ["Open", "In-Progress", "Resolved", "No-Response"]
    navigate("/agent/components/Tickets", {
      state: { filterFromDashboard: statusMap[index] },
    })
  }

  const handleInquiryPieClick = (_, index) => {
    const statusMap = ["Open", "In-Progress", "Resolved", "No-Response"]
    navigate("/agent/components/Inquiries", {
      state: { filterFromDashboard: statusMap[index] },
    })
  }

  const renderSection = (title, tone, summaryData, totalCount, onClick, navigateTo) => (
    <div className="col-12 col-xl-6">
      <div className="mobile-surface card border-0 shadow-sm h-100">
        <div className="card-body p-3 p-md-4">
          <div className="d-flex align-items-start justify-content-between mb-3">
            <div>
              <div className={`dashboard-kicker text-${tone}`}>{title}</div>
              <h5 className="mb-1 dashboard-section-title">{totalCount} active records</h5>
              <p className="text-muted small mb-0">Tap a segment or open the full list.</p>
            </div>
            <button
              type="button"
              className={`btn btn-sm btn-outline-${tone} rounded-pill d-inline-flex align-items-center gap-2`}
              onClick={() => navigate(navigateTo)}
            >
              Open list
              <FiArrowRight size={14} />
            </button>
          </div>

          <div className="row g-3 align-items-stretch">
            <div className="col-12 col-md-6">
              <div className="dashboard-summary-list">
                {summaryItems.map((item, index) => {
                  const value = summaryData[index]?.value || 0
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className="dashboard-summary-row"
                      onClick={() => onClick(null, index)}
                    >
                      <span className="dashboard-summary-icon">{item.icon}</span>
                      <span className="dashboard-summary-label">{item.label}</span>
                      <span
                        className="dashboard-summary-pill"
                        style={{
                          color: COLORS[index % COLORS.length],
                          borderColor: COLORS[index % COLORS.length],
                        }}
                      >
                        {value}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="col-12 col-md-6">
              {loading ? (
                <Skeleton height={240} />
              ) : (
                <div className="dashboard-donut-shell">
                  <div className="dashboard-donut-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={summaryData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={52}
                          outerRadius={90}
                          paddingAngle={2}
                          onClick={onClick}
                          label={renderCustomizedLabel}
                          labelLine={false}
                        >
                          {summaryData.map((entry, index) => (
                            <Cell
                              key={`${title}-${entry.name}`}
                              fill={COLORS[index % COLORS.length]}
                              stroke="#ffffff"
                              strokeWidth={3}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="dashboard-donut-center">
                    <div className={`dashboard-donut-total text-${tone}`}>{totalCount}</div>
                    <div className="text-muted small">Total {title}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="container-fluid px-2 px-md-3 py-3 py-md-4">
      <div className="dashboard-shell">
        <section className="dashboard-hero card border-0 shadow-sm overflow-hidden mb-4">
          <div className="card-body p-3 p-md-4 p-lg-5">
            <div className="row align-items-center g-4">
              <div className="col-12 col-lg-7">
                <div className="dashboard-kicker text-uppercase">Service workspace</div>
                <h1 className="dashboard-title mb-2">Staff Dashboard</h1>
                <p className="dashboard-subtitle mb-0">
                  Review assignments, open high-priority work faster, and manage follow-ups comfortably from mobile or desktop.
                </p>
              </div>
              <div className="col-12 col-lg-5">
                <div className="row g-2">
                  <div className="col-6">
                    <button
                      type="button"
                      className="dashboard-quick-link"
                      onClick={() => navigate("/agent/components/Tickets")}
                    >
                      <span className="dashboard-quick-icon">
                        <FiLayers />
                      </span>
                      <span>
                        <strong>Tickets</strong>
                        <small>Manage queue</small>
                      </span>
                    </button>
                  </div>
                  <div className="col-6">
                    <button
                      type="button"
                      className="dashboard-quick-link"
                      onClick={() => navigate("/agent/components/Inquiries")}
                    >
                      <span className="dashboard-quick-icon">
                        <FiMessageSquare />
                      </span>
                      <span>
                        <strong>Inquiries</strong>
                        <small>Track follow-ups</small>
                      </span>
                    </button>
                  </div>
                  <div className="col-6">
                    <div className="dashboard-mini-stat">
                      <span className="dashboard-mini-icon">
                        <FiActivity />
                      </span>
                      <div>
                        <strong>{totalWork}</strong>
                        <small>Total workload</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="dashboard-mini-stat">
                      <span className="dashboard-mini-icon">
                        <FiHelpCircle />
                      </span>
                      <div>
                        <strong>{totalInProgress}</strong>
                        <small>In progress</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="row g-3 mb-4">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="col-12 col-sm-6 col-xl-3">
                <Skeleton height={120} />
              </div>
            ))}
          </div>
        ) : overviewStats.length > 0 ? (
          <section className="row g-3 mb-4">
            {overviewStats.map((stat) => (
              <div key={stat.key} className="col-12 col-sm-6 col-xl-3">
                <div className={`dashboard-stat-card dashboard-stat-${stat.accent}`}>
                  <div className="dashboard-stat-icon">{stat.icon}</div>
                  <div className="dashboard-stat-label">{stat.label}</div>
                  <div className="dashboard-stat-value">{stat.value}</div>
                  <div className="dashboard-stat-sub">{stat.sublabel}</div>
                </div>
              </div>
            ))}
          </section>
        ) : null}

        {hasAssignedTickets && (
          <section className="mobile-surface card border-0 shadow-sm mb-4">
            <div className="card-body p-3 p-md-4">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <div className="dashboard-kicker text-primary">Ticket trend</div>
                  <h5 className="mb-0 dashboard-section-title">Ticket status snapshot</h5>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary rounded-pill"
                  onClick={() => navigate("/agent/components/Tickets")}
                >
                  View tickets
                </button>
              </div>

              {loading ? (
                <Skeleton height={280} />
              ) : (
                <div className="dashboard-chart-shell">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ticketBarData} margin={{ top: 16, right: 16, left: -16, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" name="Tickets" radius={[10, 10, 0, 0]} onClick={({ name }) => handleBarClick(name)}>
                        {ticketBarData.map((entry, index) => (
                          <Cell key={`bar-${entry.name}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="row g-4">
          {hasAssignedTickets &&
            renderSection(
              "Tickets",
              "primary",
              ticketSummary,
              ticketData.total_count,
              handleTicketPieClick,
              "/agent/components/Tickets"
            )}

          {hasAssignedInquiries &&
            renderSection(
              "Inquiries",
              "success",
              inquirySummary,
              inquiryData.total_count,
              handleInquiryPieClick,
              "/agent/components/Inquiries"
            )}
        </section>

        {!hasAssignedTickets && !hasAssignedInquiries && !loading && (
          <section className="mobile-surface card border-0 shadow-sm">
            <div className="card-body py-5 text-center">
              <div className="dashboard-empty-icon">
                <FiInbox />
              </div>
              <h5 className="mb-2">No work assigned yet</h5>
              <p className="text-muted mb-0">
                Tickets and inquiries assigned to your account will appear here automatically.
              </p>
            </div>
          </section>
        )}
      </div>

      <style>{`
        .dashboard-shell {
          max-width: 1400px;
          margin: 0 auto;
        }

        .dashboard-hero {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.16), transparent 38%),
            radial-gradient(circle at bottom right, rgba(13, 148, 136, 0.18), transparent 34%),
            linear-gradient(145deg, #ffffff 0%, #f7fbff 100%);
          border-radius: 28px;
        }

        .dashboard-title {
          font-size: clamp(2rem, 3vw, 3rem);
          font-weight: 800;
          letter-spacing: -0.04em;
          color: #0f172a;
        }

        .dashboard-subtitle {
          font-size: 0.98rem;
          line-height: 1.7;
          color: #475569;
          max-width: 640px;
        }

        .dashboard-kicker {
          font-size: 0.74rem;
          font-weight: 800;
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }

        .dashboard-quick-link,
        .dashboard-mini-stat {
          width: 100%;
          min-height: 88px;
          display: flex;
          align-items: center;
          gap: 14px;
          border: 1px solid rgba(148, 163, 184, 0.24);
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.92);
          padding: 14px 16px;
        }

        .dashboard-quick-link {
          text-align: left;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .dashboard-quick-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
        }

        .dashboard-quick-link strong,
        .dashboard-mini-stat strong {
          display: block;
          color: #0f172a;
          font-size: 1rem;
          line-height: 1.2;
        }

        .dashboard-quick-link small,
        .dashboard-mini-stat small {
          display: block;
          color: #64748b;
          margin-top: 4px;
        }

        .dashboard-quick-icon,
        .dashboard-mini-icon {
          width: 44px;
          height: 44px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(145deg, #dbeafe, #eff6ff);
          color: #2563eb;
          font-size: 1.15rem;
          flex-shrink: 0;
        }

        .dashboard-stat-card {
          min-height: 150px;
          border-radius: 24px;
          padding: 20px;
          color: #0f172a;
          box-shadow: 0 18px 38px rgba(15, 23, 42, 0.08);
          position: relative;
          overflow: hidden;
        }

        .dashboard-stat-card::after {
          content: "";
          position: absolute;
          inset: auto -18px -18px auto;
          width: 76px;
          height: 76px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.34);
        }

        .dashboard-stat-primary {
          background: linear-gradient(145deg, #eff6ff, #dbeafe);
        }

        .dashboard-stat-success {
          background: linear-gradient(145deg, #ecfdf5, #d1fae5);
        }

        .dashboard-stat-warning {
          background: linear-gradient(145deg, #fff7ed, #ffedd5);
        }

        .dashboard-stat-teal {
          background: linear-gradient(145deg, #f0fdfa, #ccfbf1);
        }

        .dashboard-stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 16px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
          background: rgba(255, 255, 255, 0.76);
          font-size: 1.2rem;
        }

        .dashboard-stat-label {
          color: #334155;
          font-size: 0.9rem;
          font-weight: 700;
        }

        .dashboard-stat-value {
          font-size: clamp(2rem, 4vw, 2.6rem);
          line-height: 1.05;
          font-weight: 800;
          margin: 8px 0 2px;
        }

        .dashboard-stat-sub {
          color: #64748b;
          font-size: 0.85rem;
        }

        .mobile-surface {
          border-radius: 24px;
        }

        .dashboard-section-title {
          font-weight: 700;
          color: #0f172a;
        }

        .dashboard-chart-shell {
          height: 320px;
        }

        .dashboard-summary-list {
          display: grid;
          gap: 12px;
        }

        .dashboard-summary-row {
          width: 100%;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          background: #fff;
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
        }

        .dashboard-summary-row:hover {
          border-color: #93c5fd;
          transform: translateY(-1px);
          box-shadow: 0 12px 24px rgba(37, 99, 235, 0.08);
        }

        .dashboard-summary-icon {
          width: 40px;
          height: 40px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #eff6ff;
          color: #2563eb;
          flex-shrink: 0;
        }

        .dashboard-summary-label {
          flex: 1;
          font-weight: 600;
          color: #0f172a;
          text-align: left;
        }

        .dashboard-summary-pill {
          min-width: 42px;
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid;
          font-size: 0.85rem;
          font-weight: 700;
          background: #fff;
        }

        .dashboard-donut-shell {
          position: relative;
          height: 260px;
        }

        .dashboard-donut-chart {
          width: 100%;
          height: 100%;
        }

        .dashboard-donut-center {
          position: absolute;
          inset: 50% auto auto 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          pointer-events: none;
        }

        .dashboard-donut-total {
          font-size: 1.85rem;
          line-height: 1;
          font-weight: 800;
        }

        .dashboard-empty-icon {
          width: 68px;
          height: 68px;
          border-radius: 20px;
          margin: 0 auto 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(145deg, #eff6ff, #dbeafe);
          color: #2563eb;
          font-size: 1.7rem;
        }

        @media (max-width: 991.98px) {
          .dashboard-chart-shell {
            height: 280px;
          }
        }

        @media (max-width: 767.98px) {
          .dashboard-hero,
          .mobile-surface,
          .dashboard-stat-card {
            border-radius: 22px;
          }

          .dashboard-hero .card-body {
            padding: 20px;
          }

          .dashboard-chart-shell {
            height: 240px;
          }

          .dashboard-donut-shell {
            height: 220px;
          }

          .dashboard-summary-row {
            padding: 12px 14px;
          }
        }

        @media (max-width: 575.98px) {
          .dashboard-shell {
            padding-bottom: 10px;
          }

          .dashboard-title {
            font-size: 1.9rem;
          }

          .dashboard-subtitle {
            font-size: 0.92rem;
          }

          .dashboard-stat-card {
            min-height: 132px;
          }

          .dashboard-chart-shell {
            height: 210px;
          }

          .dashboard-donut-shell {
            height: 200px;
          }

          .dashboard-quick-link,
          .dashboard-mini-stat {
            min-height: 82px;
            padding: 12px 14px;
          }
        }
      `}</style>
    </div>
  )
}

export default Dashboard
